import { Component, computed, effect, input, OnInit, signal } from '@angular/core';
import { CommonModule, NgTemplateOutlet } from '@angular/common';
import { ButtonComponent } from '../button/button.component';
import { ModalService } from '../../services/modal.service';
import { SidePanelService } from '../../services/side-panel.service';
import { StoreService } from '../../../store/store.service';

@Component({
  standalone: true,
  selector: 'app-side-panel',
  imports: [CommonModule, ButtonComponent, NgTemplateOutlet],
  templateUrl: './side-panel.component.html',
  styleUrl: './side-panel.component.scss',
})
export class SidePanelComponent implements OnInit {
  public id = input.required<string>();

  // Optional inputs for backward compatibility (can be overridden by service config)
  public title = input<string>('');
  public direction = input<'left' | 'right'>('right');
  public panelSize = input<'small' | 'medium' | 'large'>('small');
  public disableBackdropClick = input<boolean>(false);
  public hideCloseButton = input<boolean>(false);
  public contentClasses = input<string>('');
  public hideHeader = input<boolean>(false);
  public fullMaxHeight = input<boolean>(false);

  // Computed signal to check if this side panel is open
  public isOpen = computed(() => this.sidePanelSrv.sidePanelsOpen().includes(this.id()));

  // Computed signal to get the content template for this side panel
  public contentTemplate = computed(() => this.sidePanelSrv.sidePanelContent()[this.id()] || null);

  // Computed values that use service config with input fallbacks
  public effectiveTitle = computed(() => {
    return this.id() === 'global-side-panel' ? this.sidePanelSrv.sidePanelTitle() : this.title();
  });

  public effectivePanelSize = computed(() => {
    return this.id() === 'global-side-panel' ? this.sidePanelSrv.sidePanelSize() : this.panelSize();
  });

  public effectiveDirection = computed(() => {
    return this.id() === 'global-side-panel' ? this.sidePanelSrv.sidePanelDirection() : this.direction();
  });

  public effectiveDisableBackdropClick = computed(() => {
    return this.id() === 'global-side-panel' ? this.sidePanelSrv.disableBackdropClick() : this.disableBackdropClick();
  });

  public effectiveHideCloseButton = computed(() => {
    return this.id() === 'global-side-panel' ? this.sidePanelSrv.hideCloseButton() : this.hideCloseButton();
  });

  public effectiveContentClasses = computed(() => {
    return this.id() === 'global-side-panel' ? this.sidePanelSrv.contentClasses() : this.contentClasses();
  });

  public effectiveHideHeader = computed(() => {
    return this.id() === 'global-side-panel' ? this.sidePanelSrv.hideHeader() : this.hideHeader();
  });

  public effectiveFullMaxHeight = computed(() => {
    return this.id() === 'global-side-panel' ? this.sidePanelSrv.fullMaxHeight() : this.fullMaxHeight();
  });

  public zIndexOffset = computed(() => {
    const openPanels = this.sidePanelSrv.sidePanelsOpen();
    const index = openPanels.indexOf(this.id());
    return index >= 0 ? index : 0;
  });

  public isVisible = signal(false);
  public animationState = signal<'open' | 'closing'>('closing');
  public readonly isMobileLarge = computed(() => {
    return this._storeSvc.res()?.mobileLarge ?? false;
  });

  constructor(
    public modalSrv: ModalService,
    public sidePanelSrv: SidePanelService,
    public readonly _storeSvc: StoreService,
  ) {
    effect(() => {
      if (this.isOpen()) {
        this.animationState.set('open');
        this.isVisible.set(true);
        document.addEventListener('keydown', this.handleEscapeKey);

        // Set focus immediately for screen reader accessibility
        setTimeout(() => {
          const sidePanel = document.getElementById(this.id());
          if (sidePanel) {
            sidePanel.focus();
            this.modalSrv.trapFocusInContainer(sidePanel);
          }
        }, 500);
      } else if (this.isVisible()) {
          this.animationState.set('closing');
          setTimeout(() => {
            this.isVisible.set(false);
            document.removeEventListener('keydown', this.handleEscapeKey);
          }, 500);
        }
    });
  }

  ngOnInit() {
    this.isVisible.set(this.isOpen());
    if (this.isOpen()) {
      this.animationState.set('open');
    } else {
      this.animationState.set('closing');
    }
  }

  public noOfColumns = computed(() => {
    const classes = ['side-panel', 'show'];
    classes.push(`panel-width-${this.effectivePanelSize()}`);
    const dir = this._storeSvc.RTL_Flag() ? 'left' : this.effectiveDirection();
    classes.push(`panel-direction-${dir}`);
    classes.push(this.animationState());
    classes.push('h-100');
    return classes;
  });

  public handleClose = (source: any) => {
    if (source === 'btn' || (source === 'backdrop' && !this.effectiveDisableBackdropClick())) {
      this.sidePanelSrv.closeSidePanel(this.id());
    }
  };

  public handleEscapeKey = (event: any) => {
    event.key === 'Escape' && this.handleClose('backdrop');
  };
}
